import { useEffect } from "react";

export interface HreflangAlternate {
  hreflang: string;
  href: string;
}

export interface SeoConfig {
  title: string;
  description: string;
  keywords?: string;
  canonical: string;
  ogImage?: string;
  jsonLd?: object | object[];
  alternates?: HreflangAlternate[];
}

function setAlternates(alternates: HreflangAlternate[]) {
  // Remove stale hreflang tags from the previous route (SPA: no full reload).
  document
    .querySelectorAll('link[rel="alternate"][hreflang]')
    .forEach((el) => el.remove());
  for (const alt of alternates) {
    const link = document.createElement("link");
    link.rel = "alternate";
    link.setAttribute("hreflang", alt.hreflang);
    link.href = alt.href;
    document.head.appendChild(link);
  }
}
function setMeta(attr: "name" | "property", key: string, content: string) {
  let el = document.querySelector(`meta[${attr}="${key}"]`) as HTMLMetaElement | null;
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

/**
 * Sets per-route SEO tags (title, description, canonical, OG/Twitter, JSON-LD).
 * The site is a client-side SPA; Googlebot renders JS, so these tags are
 * picked up per route. Each page passes a module-level constant config.
 */
export function useSeo(cfg: SeoConfig) {
  useEffect(() => {
    document.title = cfg.title;
    document.documentElement.setAttribute("lang", "en");
    setMeta("name", "description", cfg.description);
    if (cfg.keywords) setMeta("name", "keywords", cfg.keywords);
    setMeta("property", "og:title", cfg.title);
    setMeta("property", "og:description", cfg.description);
    setMeta("property", "og:url", cfg.canonical);
    setMeta("property", "og:type", "website");
    setMeta("property", "og:site_name", "AIdeal Media");
    setMeta("name", "twitter:card", "summary_large_image");
    setMeta("name", "twitter:title", cfg.title);
    setMeta("name", "twitter:description", cfg.description);
    if (cfg.ogImage) {
      setMeta("property", "og:image", cfg.ogImage);
      setMeta("name", "twitter:image", cfg.ogImage);
    }

    let link = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!link) {
      link = document.createElement("link");
      link.rel = "canonical";
      document.head.appendChild(link);
    }
    link.href = cfg.canonical;

    // hreflang alternates (self-referencing + language versions). Stale tags
    // from the previous route are removed inside setAlternates.
    setAlternates(cfg.alternates ?? []);

    const SCRIPT_ID = "seo-jsonld";
    document.getElementById(SCRIPT_ID)?.remove();
    if (cfg.jsonLd) {
      const s = document.createElement("script");
      s.id = SCRIPT_ID;
      s.type = "application/ld+json";
      s.text = JSON.stringify(cfg.jsonLd);
      document.head.appendChild(s);
    }
    // Intentionally not cleaning up: the next route's useSeo overwrites everything.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cfg.title, cfg.canonical]);
}

export const HOME_SEO: SeoConfig = {
  title: "AIdeal Media - AI Automation Agency for SMBs | Custom AI Agents with ROI",
  description:
    "AIdeal Media is an AI automation agency for small and mid-size businesses. We design, train and deploy custom AI agents and workflows — ROI guaranteed in 90 days. Free assessment.",
  keywords: "ai automation agency, hire ai automation agency, custom ai agents, ai agents for business, aideal media",
  canonical: "https://www.aidealmedia.info/",
  ogImage: "https://www.aidealmedia.info/img/hero-bg.jpg",
  alternates: [
    { hreflang: "en", href: "https://www.aidealmedia.info/" },
    { hreflang: "es", href: "https://www.aidealmedia.info/es/" },
    { hreflang: "x-default", href: "https://www.aidealmedia.info/" },
  ],
  jsonLd: {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "AIdeal Media",
    url: "https://www.aidealmedia.info/",
    logo: "https://www.aidealmedia.info/img/logo-imagotipo-positivo.webp",
    description: "AI automation agency: custom AI agents and workflows for SMBs, with ROI guaranteed in 90 days.",
  },
};
